<template>
  <!-- prettier-ignore -->
  <div
    :class="classes"
    aria-role="button"
    aria-label="Click to select a file to upload"

    @click="open"
    @keyup.enter="open"
    @dragover="handleDragOver"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <input
      ref="filePondRef"
      hidden
      type="file"
      :accept="acceptedFileTypes"
      :multiple="allowMultiple"
      @change="change"
    />

    <slot />

  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

import { events$ } from '@/services'
import { ERRORS } from '@/helpers/FileHandler'
import { EVENT_CANCEL_DEBOUNCED_SAVING } from '@/config/events'

// Hooks

// Init
const emit = defineEmits(['error', 'update'])
const props = defineProps({
  files: {
    type: Array,
    required: true,
  },

  maxFiles: {
    type: Number,
    default: 1,
  },

  allowMultiple: {
    type: Boolean,
    default: false,
  },

  acceptedFileTypes: {
    type: String,
    default: '',
  },
})

// Data
const filePondRef = ref(null)

const dragging = ref(false)
const first = ref(false)
const second = ref(false)

// Computed
const hovering = computed(() => {
  return first.value
})

const classes = computed(() => {
  return {
    'file-drop': true,
    'file-drop--hovering': hovering.value,
    'file-drop--dragging': dragging.value,
    'file-drop--no-files': currentLength.value === 0,
  }
})

const currentLength = computed(() => {
  return props.files.length
})

// Methods
function setFiles(files) {
  if (currentLength.value + files.length <= props.maxFiles) {
    emit('update', [...files])
  } else {
    emit('error', ERRORS.MAX_FILES)
  }

  if (filePondRef?.value) {
    filePondRef.value.value = ''
  }
}

function open() {
  if (currentLength.value < props.maxFiles) {
    filePondRef.value && filePondRef.value.click()
    events$.emit(EVENT_CANCEL_DEBOUNCED_SAVING)
  }
}

function cancel() {
  first.value = false
  second.value = false
}

function change(e) {
  // e.preventDefault()
  // e.stopPropagation()
  const { files } = e.target
  if (files && files.length > 0) {
    setFiles(files)
  }
}

function handleDragOver(e) {
  e.preventDefault()
}

function handleDragEnter() {
  if (first.value) {
    second.value = true
  } else {
    first.value = true
  }
}

function handleDragLeave() {
  if (second.value) {
    second.value = false
  } else if (first.value) {
    first.value = false
  }
}

function handleDrop(e) {
  e.preventDefault()

  setFiles(e.dataTransfer.files)
  cancel()
}
</script>

<style scoped lang="postcss">
.file-drop {
  display: flex;
  height: 150px;
  margin: 1rem auto;
  padding: 15px;
  justify-content: center;
  align-items: center;
  background-color: var(--c-warm-gray);

  &:hover {
    cursor: inherit;

    &.file-drop--no-files {
      cursor: pointer;
    }
  }

  &--dragging {
    border: 2px solid var(--c-sanitas-green);
  }

  &--hovering {
    border: 1px solid var(--c-sanitas-green);
  }
}
</style>
